import React, { useState, useEffect } from 'react';
import { Table, Button, Container, Row, Col, Pagination, Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import RequestDetailsModal from '../components/RequestDetailsModal';
import ConfirmModal from '../components/ConfirmModal';
import { getAllOtRequests, deleteRequest, activateRequest, permanentDeleteRequest } from '../services/Services';

function ManageRequests() {
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [showConfirmModal, setShowConfirmModal] = useState(false);

    const [confirmConfig, setConfirmConfig] = useState({
        title: '',
        message: '',
        onConfirm: null,
        variant: 'primary',
        confirmText: 'Confirmar',
        cancelText: 'Cancelar'
    });

    const [pagination, setPagination] = useState({
        total: 0,
        currentPage: 1,
        totalPages: 0,
        limit: 20,
        hasMore: false
    });

    const fetchData = async (page = 1, limit = 20) => {
        setLoading(true);
        setError(null);
        try {
            const response = await getAllOtRequests(limit, page);
            if (response.success) {
                setData(response.data);
                setPagination(response.pagination);
            } else {
                setError('Error al cargar los datos');
            }
        } catch (error) {
            setError('Error de conexión');
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, currentPage: page }));
        fetchData(page, pagination.limit);
    };

    const handleLimitChange = (e) => {
        const newLimit = parseInt(e.target.value);
        setPagination(prev => ({ ...prev, limit: newLimit, currentPage: 1 }));
        fetchData(1, newLimit);
    };

    const handleViewDetails = (id) => {
        setSelectedRequestId(id);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
        setSelectedRequestId(null);
    };

    const handleCloseConfirmModal = () => {
        setShowConfirmModal(false);
        setConfirmConfig({
            title: '',
            message: '',
            onConfirm: null,
            variant: 'primary',
            confirmText: 'Confirmar',
            cancelText: 'Cancelar'
        });
    };

    const handleEdit = (id) => {
        navigate(`/overtimerequest/${id}`);
    };

    const handleToggleActive = async (id, currentStatus) => {
        const action = currentStatus ? 'desactivar' : 'activar';

        setConfirmConfig({
            title: 'Confirmar acción',
            message: `¿Confirmar ${action} esta solicitud?`,
            variant: currentStatus ? 'warning' : 'success',
            onConfirm: async () => {
                try {
                    if (currentStatus) {
                        // Si está activo, desactivar usando deleteRequest
                        const result = await deleteRequest(id);
                        if (result.result) {
                            toast.success('Solicitud desactivada exitosamente');
                            // Recargar los datos para reflejar el cambio
                            fetchData(pagination.currentPage, pagination.limit);
                        } else {
                            toast.error('Error al desactivar la solicitud');
                        }
                    } else {
                        // Si está inactivo, activar usando activateRequest
                        const result = await activateRequest(id);
                        if (result.result) {
                            toast.success('Solicitud activada exitosamente');
                            // Recargar los datos para reflejar el cambio
                            fetchData(pagination.currentPage, pagination.limit);
                        } else {
                            toast.error('Error al activar la solicitud');
                        }
                    }
                } catch (error) {
                    console.error('Error toggling active status:', error);
                    toast.error('Error al cambiar el estado de la solicitud');
                }
            }
        });
        setShowConfirmModal(true);
    };

    const handleDelete = async (id) => {
        setConfirmConfig({
            title: '⚠️ ELIMINACIÓN PERMANENTE',
            message: '¿Estás COMPLETAMENTE SEGURO de que deseas ELIMINAR PERMANENTEMENTE esta solicitud? Esta acción NO SE PUEDE DESHACER y eliminará todos los datos asociados.',
            variant: 'danger',
            confirmText: 'SÍ, ELIMINAR PERMANENTEMENTE',
            cancelText: 'Cancelar',
            onConfirm: async () => {
                try {
                    const result = await permanentDeleteRequest(id);
                    if (result.result) {
                        toast.success('Solicitud eliminada permanentemente');
                        // Recargar los datos para reflejar el cambio
                        fetchData(pagination.currentPage, pagination.limit);
                    } else {
                        toast.error('Error al eliminar la solicitud permanentemente');
                    }
                } catch (error) {
                    console.error('Error deleting request permanently:', error);
                    toast.error('Error al eliminar la solicitud permanentemente');
                }
            }
        });
        setShowConfirmModal(true);
    };

    useEffect(() => {
        fetchData();
    }, []);

    if (loading) return <Loading />;
    if (error) return <div className="alert alert-danger">{error}</div>;

    // Generar items de paginación
    const paginationItems = [];
    const maxVisiblePages = 5;
    const startPage = Math.max(1, pagination.currentPage - Math.floor(maxVisiblePages / 2));
    const endPage = Math.min(pagination.totalPages, startPage + maxVisiblePages - 1);

    // Primera página
    if (startPage > 1) {
        paginationItems.push(
            <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
                1
            </Pagination.Item>
        );
        if (startPage > 2) {
            paginationItems.push(<Pagination.Ellipsis key="start-ellipsis" />);
        }
    }

    // Páginas visibles
    for (let page = startPage; page <= endPage; page++) {
        paginationItems.push(
            <Pagination.Item
                key={page}
                active={page === pagination.currentPage}
                onClick={() => handlePageChange(page)}
            >
                {page}
            </Pagination.Item>
        );
    }

    // Última página
    if (endPage < pagination.totalPages) {
        if (endPage < pagination.totalPages - 1) {
            paginationItems.push(<Pagination.Ellipsis key="end-ellipsis" />);
        }
        paginationItems.push(
            <Pagination.Item
                key={pagination.totalPages}
                onClick={() => handlePageChange(pagination.totalPages)}
            >
                {pagination.totalPages}
            </Pagination.Item>
        );
    }

    return (
        <Container fluid>
            <Row className="mb-3 align-items-end">
                <Col xs={12} lg={3} className="mb-2 mb-lg-0">
                    <h2>Gestionar Solicitudes</h2>
                </Col>
                <Col xs={12} sm={7} lg={4} className="mb-2 mb-sm-0">
                    <Form.Group>
                        <Form.Label>Registros por página:</Form.Label>
                        <Form.Select
                            value={pagination.limit}
                            onChange={handleLimitChange}
                            style={{ width: '120px' }}
                        >
                            <option value={10}>10</option>
                            <option value={20}>20</option>
                            <option value={50}>50</option>
                            <option value={100}>100</option>
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col xs={12} sm={6} lg={4} className="d-flex align-items-end justify-content-sm-end">
                    <span className="text-muted">
                        Mostrando {((pagination.currentPage - 1) * pagination.limit) + 1} - {Math.min(pagination.currentPage * pagination.limit, pagination.total)} de {pagination.total} registros
                    </span>
                </Col>
            </Row>
            <Row>
                <Col>
                    <Table striped bordered hover responsive>
                        <thead className="table-primary">
                            <tr>
                                <th className="text-center">ID</th>
                                <th className="text-center">Empleado</th>
                                <th className="text-center">Email Empleado</th>
                                <th className="text-center">Solicitado Por</th>
                                <th className="text-center">Email Solicitante</th>
                                <th className="text-center">Estado</th>
                                <th className="text-center">Activo</th>
                                <th className="text-center">Período</th>
                                <th className="text-center">Fecha Creación</th>
                                <th className="text-center">Fecha Aprobación</th>
                                <th className="text-center">Aprobador</th>
                                <th className="text-center">Costo/Hora</th>
                                <th className="text-center">Pago Semanal</th>
                                <th className="text-center">Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.map((request) => (
                                <tr key={request.id} className={!request.activo ? 'table-secondary' : ''}>
                                    <td className="text-center">{request.id}</td>
                                    <td className="text-center">
                                        {request.empleado || 'N/A'}
                                    </td>
                                    <td className="text-center">
                                        {request.empleadoEmail || 'N/A'}
                                    </td>
                                    <td className="text-center">
                                        {request.solicitadoPor || 'N/A'}
                                    </td>
                                    <td className="text-center">
                                        {request.solicitadoPorEmail || 'N/A'}
                                    </td>
                                    <td className="text-center">
                                        <span className={`badge bg-${request.statusColor || 'secondary'}`}>
                                            {request.status || 'N/A'}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        <span className={`badge ${request.activo ? 'bg-success' : 'bg-danger'}`}>
                                            {request.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </td>
                                    <td className="text-center">
                                        {request.fechaInicio && request.fechaFin ?
                                            `${request.fechaInicio} - ${request.fechaFin}` :
                                            'N/A'
                                        }
                                    </td>
                                    <td className="text-center">
                                        {request.fechaCreacion || 'N/A'}
                                    </td>
                                    <td className="text-center">
                                        {request.fechaAprobacion || 'N/A'}
                                    </td>
                                    <td className="text-center">
                                        {request.aprobadoPor || 'No aprobado'}
                                        {request.aprobadoPorEmail && request.aprobadoPorEmail !== 'noemail@noemail.com' && (
                                            <>
                                                <br />
                                                <small className="text-muted">({request.aprobadoPorEmail})</small>
                                            </>
                                        )}
                                    </td>
                                    <td className="text-center">
                                        ${request.costoHoraExtra || 0}
                                    </td>
                                    <td className="text-center">
                                        ${request.pagoSemanal || 0}
                                    </td>
                                    <td className="text-center">
                                        <Button
                                            variant="info"
                                            size="sm"
                                            className="me-1 mb-1"
                                            onClick={() => handleViewDetails(request.id)}
                                        >
                                            Ver
                                        </Button>
                                        <br />
                                        <Button
                                            variant={request.activo ? "warning" : "success"}
                                            size="sm"
                                            className="me-1 mb-1"
                                            onClick={() => handleToggleActive(request.id, request.activo)}
                                        >
                                            {request.activo ? 'Desactivar' : 'Activar'}
                                        </Button>
                                        <br />
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleDelete(request.id)}
                                        >
                                            Eliminar
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
            {pagination.totalPages > 1 && (
                <Row className="mt-3">
                    <Col className="d-flex justify-content-center">
                        <Pagination>
                            <Pagination.Prev
                                disabled={pagination.currentPage === 1}
                                onClick={() => handlePageChange(pagination.currentPage - 1)}
                            />
                            {paginationItems}
                            <Pagination.Next
                                disabled={pagination.currentPage === pagination.totalPages}
                                onClick={() => handlePageChange(pagination.currentPage + 1)}
                            />
                        </Pagination>
                    </Col>
                </Row>
            )}
            {/* Modal de detalles */}
            <RequestDetailsModal
                show={showDetailsModal}
                handleClose={handleCloseDetailsModal}
                requestId={selectedRequestId}
            />
            {/* Modal de confirmación */}
            <ConfirmModal
                show={showConfirmModal}
                handleClose={handleCloseConfirmModal}
                onConfirm={confirmConfig.onConfirm}
                title={confirmConfig.title}
                message={confirmConfig.message}
                variant={confirmConfig.variant}
                confirmText={confirmConfig.confirmText}
                cancelText={confirmConfig.cancelText}
            />
        </Container>
    );
}

export default ManageRequests;